import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, Trash2, X } from 'lucide-react';
import { Button } from './ui/button';

export default function NotificationBell() {
  const { user, axiosAuth } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const api = axiosAuth();

  const fetchUnreadCount = async () => {
    try {
      const r = await api.get('/notifications/unread-count');
      setUnreadCount(r.data.unread_count);
    } catch {}
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const r = await api.get('/notifications?limit=20');
      setNotifications(r.data.notifications);
      setUnreadCount(r.data.unread_count);
    } catch {}
    setLoading(false);
  };

  useEffect(() => {
    if (!user) return;
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markRead = async (id) => {
    await api.put(`/notifications/${id}/read`);
    setNotifications(n => n.map(x => x.id === id ? { ...x, read: true } : x));
    setUnreadCount(c => Math.max(0, c - 1));
  };

  const markAllRead = async () => {
    await api.put('/notifications/read-all');
    setNotifications(n => n.map(x => ({ ...x, read: true })));
    setUnreadCount(0);
  };

  const deleteNotif = async (id, wasUnread) => {
    await api.delete(`/notifications/${id}`);
    setNotifications(n => n.filter(x => x.id !== id));
    if (wasUnread) setUnreadCount(c => Math.max(0, c - 1));
  };

  const handleClick = (notif) => {
    if (!notif.read) markRead(notif.id);
    if (notif.link) { navigate(notif.link); setOpen(false); }
  };

  const timeAgo = (dateStr) => {
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const ICON_COLORS = {
    calendar: 'bg-blue-100 text-blue-600',
    message: 'bg-violet-100 text-violet-600',
    target: 'bg-emerald-100 text-emerald-600',
    flag: 'bg-amber-100 text-amber-600',
    heart: 'bg-rose-100 text-rose-600',
    trophy: 'bg-yellow-100 text-yellow-600',
    'user-check': 'bg-green-100 text-green-600',
    'user-x': 'bg-red-100 text-red-600',
    users: 'bg-indigo-100 text-indigo-600',
    'user-plus': 'bg-teal-100 text-teal-600',
    'alert-triangle': 'bg-red-100 text-red-600',
    clipboard: 'bg-sky-100 text-sky-600',
    route: 'bg-purple-100 text-purple-600',
    home: 'bg-orange-100 text-orange-600',
    info: 'bg-slate-100 text-slate-600',
  };

  if (!user) return null;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-full hover:bg-teal-50 transition-colors"
        data-testid="notification-bell"
      >
        <Bell size={20} className="text-slate-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1" data-testid="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-[360px] max-h-[480px] bg-white rounded-xl shadow-xl border border-border z-50 overflow-hidden" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-slate-50/80">
            <h3 className="text-sm font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>Notifications</h3>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <Button variant="ghost" size="sm" className="h-7 text-xs text-teal-600 hover:text-teal-700 gap-1" onClick={markAllRead} data-testid="mark-all-read">
                  <CheckCheck size={13} /> Mark all read
                </Button>
              )}
              <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-slate-200"><X size={14} /></button>
            </div>
          </div>

          <div className="overflow-y-auto max-h-[400px]">
            {loading && notifications.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell size={32} className="mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-muted-foreground">No notifications yet</p>
              </div>
            ) : (
              notifications.map(n => (
                <div
                  key={n.id}
                  className={`flex items-start gap-3 px-4 py-3 border-b border-border/50 cursor-pointer transition-colors ${n.read ? 'bg-white hover:bg-slate-50' : 'bg-teal-50/40 hover:bg-teal-50/70'}`}
                  onClick={() => handleClick(n)}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${ICON_COLORS[n.icon] || 'bg-slate-100 text-slate-600'}`}>
                    <Bell size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs leading-snug ${n.read ? 'text-slate-600' : 'text-slate-800 font-medium'}`}>
                      {n.message}
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-1">{timeAgo(n.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0">
                    {!n.read && (
                      <button onClick={(e) => { e.stopPropagation(); markRead(n.id); }} className="p-1 rounded hover:bg-teal-100" title="Mark read">
                        <Check size={12} className="text-teal-600" />
                      </button>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); deleteNotif(n.id, !n.read); }} className="p-1 rounded hover:bg-red-100" title="Delete">
                      <Trash2 size={12} className="text-slate-400 hover:text-red-500" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {notifications.length > 0 && (
            <div className="border-t border-border px-4 py-2 bg-slate-50/80">
              <button onClick={() => { navigate('/notifications'); setOpen(false); }} className="text-xs text-teal-600 hover:text-teal-700 font-medium" data-testid="view-all-notifications">
                View All Notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
